/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  GrabXDataTypes,
  GrabXFeatureFlag,
  GrabXRTKQueryMigrationEndpointsFlag,
} from "./types"

export const EMPTY_VARIABLE_NAME = "empty variable name"

export const DEFAULT_RTK_QUERY_MIGRATION_ENDPOINT_FLAGS: GrabXRTKQueryMigrationEndpointsFlag = {
  searchOutlets: false,
  searchStore: false,
  fetchMerchantDetail: false,
  fetchItemsWithoutPhotoCount: false,
  fetchIncidentStat: false,
  submitReportReview: false,
  postMerchantReviewThrottle: false,
  fetchMerchantAggregate: false,
  fetchIncidentList: false,
  getIncident: false,
  loadConfig: false,
  uploadReportReviewImg: false,
  fetchOrderEta: false,
  fetchCityList: false,
  fetchOrderCancelReasons: false,
  fetchOrderSummary: false,
  reviewOrderChanges: false,
  submitOrderChanges: false,
  cancelOrder: false,
  fetchOrderExportedMpaFile: false,
  fetchOrderExportedFile: false,
  fetchStoreDetailFromMaxAPI: false,
  fetchOrderByID: false,
  updateStoreStatus: false,
  markOrder: false,
  deleteCategory: false,
  changeModifierAvailability: false,
  createMenuGroup: false,
  upsertItemV2: false,
  publishMenuGroup: false,
  deleteMenuGroup: false,
  deleteItem: false,
  getTranslation: false,
  linkItemToModifierGroup: false,
  completeBulkTask: false,
  createBulkTask: false,
  resetMenuGroup: false,
  fetchInventoryExportedFile: false,
  searchItems: false,
  editModifierGroup: false,
  fetchDelvPlatformFeatureFlags: false,
  fetchInventoryCategory: false,
  getUserProfileDetail: false,
  fetchStoreOpenStatus: false,
  fetchDepartmentsForGrabCategory: false,
  getVerifyTranslation: false,
  createModifierGroup: false,
  getSingleTranslation: false,
  deleteModifierGroup: false,
  fetchMenuGroupList: false,
  fetchSubdepartmentsForGrabCategory: false,
  proofModifierGroup: false,
  updateExistingTranslation: false,
  unLinkItemFromModifierGroup: false,
  sortDepartments: false,
  sortItems: false,
  sortCategories: false,
  createCategory: false,
  linkStoreToMenuGroup: false,
  uploadItemPhoto: false,
  createSection: false,
  deleteSection: false,
  unlinkStoreFromMenuGroup: false,
  sortDepartmentItems: false,
  fetchBulkTasks: false,
  postFeedbackGrabCategory: false,
  updateItemsAvailableStatus: false,
  fetchSupportedAttributeCluster: false,
  fetchStoreList: false,
  fetchAttributeClusterOption: false,
  updateSellingTime: false,
  updateDepartment: false,
  updateMenuGroup: false,
  updateCategory: false,
  updateSections: false,
  createSellingTime: false,
  deleteSellingTime: false,
  updateItemsSellingTime: false,
  enhanceImage: false,
  fetchListBrandsSearch: false,
  fetchMenuGroupLinkedStores: false,
  loadMoreDepartmentsForGrabCategory: false,
}

Object.freeze(DEFAULT_RTK_QUERY_MIGRATION_ENDPOINT_FLAGS)

export const createRtkQueryMigrationEndpointFlags = (
  overrides: Partial<GrabXRTKQueryMigrationEndpointsFlag> = {},
): GrabXRTKQueryMigrationEndpointsFlag => ({
  ...DEFAULT_RTK_QUERY_MIGRATION_ENDPOINT_FLAGS,
  ...overrides,
})

export enum MerchantPortalFeatureFlag {
  markOrdersReadyConfig = "markOrdersReadyConfig",
  enableMenuTranslationPortal = "enableMenuTranslationPortal",
  enableMenuGroupMenuTranslationPortal = "enableMenuGroupMenuTranslationPortal",
  brandListing = "enableBrandListingTroyPortal",
  attributeLocalizationEnabled = "IsAttributeLocalizationEnabled",
  portalRetryTimeout = "portalRetryTimeout",
  enableMexSupplier = "enableMexSupplier",
  enableAlcoholSetting = "SelfServeAlcoholTaggingEnabled",
  enableAlcoholSettingMenuGroupV2 = "SelfServeAlcoholTaggingEnabledForMenuGroupV2",
  linkStoreMaxLimit = "linkStoreMaxLimit",
  portalEnableHermesConnection = "portalEnableHermesConnection",
  portalEnablePartialMenu = "portalEnablePartialMenu",
  // BP-50303: gates the store-listing-page BCRS nudge banner via GrabX (mem facet)
  enableBCRSPortalNudge = "enableBCRSPortalNudge",
  // BP-55344: gates BCRS surfaces on the Menu Group V2 editor. Fetched on-demand
  // with country/merchant facets (like the alcohol/tobacco MenuGroupV2 flags),
  // since the menu-group page has no merchant.featureFlags/outletV2 to read from.
  enableBCRSForMenuGroupV2 = "enableBCRSForMenuGroupV2",
  enableTrackError = "enablePortalTrackError",
  // owner: @leyi.song expected rolout: Jan 2025
  TobaccoSaleEnabled = "TobaccoSaleEnabled",
  TobaccoSaleEnabledForMenuGroupV2 = "TobaccoSaleEnabledForMenuGroupV2",
  // owner: @muhammad.arfandy, don't delete after full rollout
  portalDatadogRumConfig = "portalDatadogRumConfig",
  // owner: @husni.habil, don't delete after full rollout
  enableEditorUIBulkUpload = "enableEditorUIBulkUpload",
  // owner: @novi.steven expected rollout: Oct 2025
  portalEnableBulkUploadProcessingBannerByUploadType = "portalEnableBulkUploadProcessingBannerByUploadType",
  // owner: @muhammad.arfandy expected rollout: Oct 2025, can't be removed after full rollout, its also can be used to check if the store is grab devices store or not
  portalEnableDownloadMpaTransaction = "portalEnableDownloadMpaTransaction",
  // owner: @novi.steven
  portalEnableGrabMexProImageEditor = "portalEnableGrabMexProImageEditor",
  // owner: @wesley.loh expected rollout: Feb 2026
  portalEnableHideItem = "portalEnableHideItem",
  // owner: @husni.habil, list of couriers for mex supplier, not expected to remove
  mexSupplierCourierList = "MexSupplierCourierList",
  // owner: @husni.habil
  rtkQueryMigrationEndpointsFlag = "rtkQueryMigrationEndpointsFlag",
  // owner: @dendi.abdul expected rollout: Jul 2026
  portalEnableEInvoiceCarrierCode = "portalEnableEInvoiceCarrierCode",
  // owner: @ecommerce-web expected rollout: Jun 2026
  enableEcomAttributeLocalization = "enableEcomAttributeLocalization",
  // owner: @ecommerce-web
  portalEcomAssetsMobilePath = "portalEcomAssetsMobilePath",
  // owner: @m.hidayat expected rollout: Q4 2026 (BP-54649)
  portalEnableEcomReturnRefund = "portalEnableEcomReturnRefund",
}

// list of grabX variable to be pulled
export const GRABX_FLAG_LIST: GrabXFeatureFlag[] = [
  {
    grabxVariableName: MerchantPortalFeatureFlag.brandListing,
    dataType: GrabXDataTypes.Boolean,
    defaultValue: false,
  },
  {
    grabxVariableName: MerchantPortalFeatureFlag.attributeLocalizationEnabled,
    dataType: GrabXDataTypes.Boolean,
    defaultValue: false,
  },
  {
    grabxVariableName: MerchantPortalFeatureFlag.portalRetryTimeout,
    dataType: GrabXDataTypes.Number,
    defaultValue: 0,
  },
  {
    grabxVariableName: MerchantPortalFeatureFlag.linkStoreMaxLimit,
    dataType: GrabXDataTypes.Number,
    defaultValue: 50,
  },
  {
    grabxVariableName: MerchantPortalFeatureFlag.enableTrackError,
    dataType: GrabXDataTypes.Boolean,
    defaultValue: false,
  },
  {
    grabxVariableName: MerchantPortalFeatureFlag.portalEnableHideItem,
    dataType: GrabXDataTypes.Boolean,
    defaultValue: false,
  },
  {
    grabxVariableName: MerchantPortalFeatureFlag.mexSupplierCourierList,
    dataType: GrabXDataTypes.Json,
    defaultValue: [],
  },
  {
    grabxVariableName:
      MerchantPortalFeatureFlag.portalEnableEInvoiceCarrierCode,
    dataType: GrabXDataTypes.Boolean,
    defaultValue: false,
  },
  {
    grabxVariableName:
      MerchantPortalFeatureFlag.enableEcomAttributeLocalization,
    dataType: GrabXDataTypes.Boolean,
    defaultValue: false,
  },
  {
    grabxVariableName: MerchantPortalFeatureFlag.portalEcomAssetsMobilePath,
    dataType: GrabXDataTypes.Json,
    defaultValue: {
      bulkAddProducts:
        "mex/portal/ecommerce/v6/eCommerce-Bulk-Upload-Template.zip",
    },
  },
  {
    grabxVariableName: MerchantPortalFeatureFlag.rtkQueryMigrationEndpointsFlag,
    dataType: GrabXDataTypes.Json,
    defaultValue: DEFAULT_RTK_QUERY_MIGRATION_ENDPOINT_FLAGS,
  },
]
